import * as runtime from './runtime-support.mjs';

/** 
 * Logic that supports the loading of scripts into the browser client.
 */

/**
 * Append a list of script libraries, one by one, to the DOM. Implementation is based on a series 
 * of cascading onloads, with each downstream script pulled-in upon the onload of the preceeding 
 * upstream script. Each script library may, optionally, be paired with a stylesheet.
 * 
 * Originally inspired by the following: 
 * https://philipwalton.com/articles/loading-polyfills-only-when-needed/#loading-the-polyfills
 *
 * @param {(string[]|Object[])} scriptSrcArr - Array of script sources. Each entry may either be 
 *  a simple string or an object with "script" and "style" properties. If a simple string, the 
 *  value is assumed to be for the source URL of a script.
 * @param {string} scriptSrcArr[].script - To be used as the "src" value of a generated "script" 
 *  element.
 * @param {string} scriptSrcArr[].style - To be used as the "href" value of a generated "link" 
 *  stylesheet element.
 */
export const appendScriptsUsingCascadingLoads = function(scriptSrcArr) {
    let scriptSrc = null;
    let styleSrc = null;

    const scriptSrcEntry = scriptSrcArr.shift();
    if (typeof scriptSrcEntry === 'object') {
        scriptSrc = scriptSrcEntry.script;
        styleSrc = scriptSrcEntry.style;
    } else {
        scriptSrc = scriptSrcEntry;
    }

    let scriptEle = document.createElement('script');
    scriptEle.type = 'text/javascript';
    scriptEle.src = scriptSrc;

    if (scriptSrcArr.length > 0) {
        // The load event is triggered after the script is loaded and executed.
        scriptEle.onload = function() {
            //console.log('onload for %s', scriptEle.src);
            appendScriptsUsingCascadingLoads(scriptSrcArr);
        };
    }

    scriptEle.onerror = handleScriptLoadError;
    // Force the scripts to be executed in document order, as when using 'defer'.
    scriptEle.setAttribute('async', false);
    //console.log('appending %s', scriptEle.src);
    /*
     * Each script will start loading as soon as it is appended to the document. More info: 
     *  https://javascript.info/script-async-defer#dynamic-scripts
     */
    document.head.appendChild(scriptEle);
    //console.log('appended %s', scriptEle.src);

    if (styleSrc !== null && typeof styleSrc !== 'undefined') {
        /*
         * If specified, also append a stylesheet reference to the document.
         * For more sophistication, see https://github.com/filamentgroup/loadCSS
         * However, loadCSS is not importable.
         */
        let styleEle = document.createElement('link');
        styleEle.rel = 'stylesheet';
        styleEle.media = 'all';
        styleEle.href = styleSrc;
        document.head.appendChild(styleEle);
    }
};

/**
 * Handle an error raised while loading a script file.
 */
export const handleScriptLoadError = function(errorEvent) {
    console.error('errorEvent = %o', errorEvent);
    throw new URIError('Unable to load script ' + errorEvent.target.src);
};
